import { useTranslate, HttpError } from '@refinedev/core';
import { Edit, useForm, useSelect } from '@refinedev/antd';
import { Form, Input, Select, Checkbox, Divider } from 'antd';
import type { SelectProps } from 'antd';
import type {
  PublicationDetailOut,
  PublicationUpdateIn,
} from '@/api/generated/schemas';
import {
  PublicationWarning,
  PublicationAudience,
  CollectionData,
  ManagedCollectionKind,
  ImageKind,
} from '@/api/generated/schemas';
import { ImageUploadField } from '@/components/common/ImageUploadField';
import { SearchPopularityField } from '@/components/common/SearchPopularityField';
import {
  EXCLUSION_FAMILIES,
  allowedForFamily,
  pruneExcluded,
} from './excludedIds';
import type { ExclusionFamily } from './excludedIds';

export const PublicationEdit = () => {
  const t = useTranslate();
  const { formProps, saveButtonProps, form, query } = useForm<
    PublicationDetailOut,
    HttpError,
    PublicationUpdateIn
  >({
    resource: 'user_catalog.publications',
    mutationMeta: { method: 'patch' },
    redirect: 'show',
  });

  const record = query?.data?.data;

  // Watched static pins per family — exclusions may only target memberships,
  // so their option set follows the pins edited in this form.
  const watchedStaticCategoryIds = Form.useWatch('static_category_ids', form);
  const watchedStaticCollectionIds = Form.useWatch(
    'static_collection_ids',
    form
  );
  const watchedStaticTagIds = Form.useWatch('static_custom_tag_ids', form);

  // Autocomplete for categories (static assignments only)
  const { selectProps: categorySelectProps } = useSelect({
    resource: 'user_catalog.categories',
    optionLabel: 'name',
    optionValue: 'id',
    onSearch: (value) => [
      { field: 'q', operator: 'contains', value },
      {
        field: 'collection_data',
        operator: 'eq',
        value: CollectionData.static,
      },
    ],
    defaultValue: record?.static_category_ids,
  });

  // Autocomplete for managed collections (static assignments only)
  const { selectProps: collectionSelectProps } = useSelect({
    resource: 'user_catalog.managed_collections',
    optionLabel: 'name_with_kind',
    optionValue: 'id',
    onSearch: (value) => [
      { field: 'q', operator: 'contains', value },
      {
        field: 'collection_data',
        operator: 'eq',
        value: CollectionData.static,
      },
      {
        field: 'kind',
        operator: 'in',
        value: [
          ManagedCollectionKind.subcategory,
          ManagedCollectionKind.editorial,
        ],
      },
    ],
    defaultValue: record?.static_collection_ids,
  });

  // Autocomplete for tags (static assignments only)
  const { selectProps: tagSelectProps } = useSelect({
    resource: 'user_catalog.tags',
    optionLabel: 'name',
    optionValue: 'id',
    onSearch: (value) => [
      { field: 'q', operator: 'contains', value },
      {
        field: 'collection_data',
        operator: 'eq',
        value: CollectionData.static,
      },
    ],
    defaultValue: record?.static_custom_tag_ids,
  });

  const familyContext = (
    family: ExclusionFamily
  ): {
    staticIds: number[] | null | undefined;
    remoteOptions: SelectProps['options'];
  } => {
    switch (family.key) {
      case 'categories':
        return {
          staticIds: watchedStaticCategoryIds,
          remoteOptions: categorySelectProps.options,
        };
      case 'collections':
        return {
          staticIds: watchedStaticCollectionIds,
          remoteOptions: collectionSelectProps.options,
        };
      case 'tags':
        return {
          staticIds: watchedStaticTagIds,
          remoteOptions: tagSelectProps.options,
        };
    }
  };

  // Excluded ids must be members (static pins as edited ∪ dynamic ∪ currently
  // excluded) — otherwise the backend rejects the PATCH with 422. Options are
  // local: names come from the record's nested membership objects, with the
  // remote pin options as a fallback for pins added in this session.
  const excludedSelect = (family: ExclusionFamily | undefined) => {
    if (!record || !family) return null;
    const { staticIds, remoteOptions } = familyContext(family);
    const allowed = allowedForFamily(record, family, staticIds);
    const names = new Map<number, string>();
    (record[family.membersField] ?? []).forEach((m) => {
      if (m.name) names.set(m.id, m.name);
    });
    (remoteOptions ?? []).forEach((o) => {
      if (typeof o.value === 'number' && !names.has(o.value)) {
        names.set(o.value, String(o.label));
      }
    });
    const options = [...allowed]
      .map((id) => ({ value: id, label: names.get(id) ?? `#${id}` }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return (
      <Form.Item
        name={family.excludedField}
        label={t(`user_catalog.publications.fields.excluded_${family.key}`)}
        extra={t('user_catalog.publications.fields.excluded_hint')}
      >
        <Select
          mode="multiple"
          allowClear
          showSearch
          optionFilterProp="label"
          options={options}
          placeholder={t(
            `user_catalog.publications.fields.excluded_${family.key}_placeholder`
          )}
        />
      </Form.Item>
    );
  };

  // A pin removed in this edit may still sit in the excluded list — prune it so
  // the request stays valid.
  const handleValuesChange = (
    changed: Partial<PublicationUpdateIn>,
    all: PublicationUpdateIn
  ) => {
    if (!record) return;
    EXCLUSION_FAMILIES.forEach((family) => {
      if (!(family.staticField in changed)) return;
      const allowed = allowedForFamily(record, family, all[family.staticField]);
      const current = all[family.excludedField] ?? [];
      const pruned = pruneExcluded(current, allowed);
      if (pruned.length !== current.length) {
        form?.setFieldValue(family.excludedField, pruned);
      }
    });
  };

  const warningOptions = [
    {
      label: t('user_catalog.publications.warnings.profanity'),
      value: PublicationWarning.NUMBER_1,
    },
    {
      label: t('user_catalog.publications.warnings.violence'),
      value: PublicationWarning.NUMBER_2,
    },
    {
      label: t('user_catalog.publications.warnings.adult_content'),
      value: PublicationWarning.NUMBER_4,
    },
  ];

  return (
    <Edit saveButtonProps={saveButtonProps}>
      <Form
        {...formProps}
        layout="vertical"
        onValuesChange={(changed, all) => {
          formProps.onValuesChange?.(changed, all);
          handleValuesChange(changed, all);
        }}
      >
        {/* Basic information */}
        <Divider orientation="left">
          {t('user_catalog.publications.sections.basic_info')}
        </Divider>

        <Form.Item
          name="title"
          label={t('user_catalog.publications.fields.title')}
          rules={[{ required: true }, { max: 500 }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="subtitle"
          label={t('user_catalog.publications.fields.subtitle')}
          rules={[{ max: 500 }]}
          normalize={(value) => (value === '' ? null : value)}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="description"
          label={t('user_catalog.publications.fields.description')}
          normalize={(value) => (value === '' ? null : value)}
        >
          <Input.TextArea rows={6} />
        </Form.Item>

        <Form.Item name="large_cover_storage_key" hidden>
          <Input />
        </Form.Item>
        <ImageUploadField
          imageKind={ImageKind.covers}
          currentUrl={record?.large_cover_url}
          onUpload={(key) =>
            form?.setFieldsValue({ large_cover_storage_key: key })
          }
          label={t('user_catalog.publications.fields.large_cover_url')}
        />

        <Form.Item
          name="audience"
          label={t('user_catalog.publications.fields.audience')}
        >
          <Select
            style={{ width: 200 }}
            options={Object.values(PublicationAudience).map((value) => ({
              label: t(`user_catalog.publications.audience.${value}`),
              value,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="warnings"
          label={t('user_catalog.publications.fields.warnings')}
        >
          <Checkbox.Group options={warningOptions} />
        </Form.Item>

        {/* Categories & collections */}
        <Divider orientation="left">
          {t('user_catalog.publications.sections.categories_collections')}
        </Divider>

        <Form.Item
          name="static_category_ids"
          label={t('user_catalog.publications.fields.categories')}
        >
          <Select
            {...categorySelectProps}
            mode="multiple"
            showSearch
            allowClear
            placeholder={t(
              'user_catalog.publications.fields.categories_placeholder'
            )}
            filterOption={false}
          />
        </Form.Item>
        {excludedSelect(EXCLUSION_FAMILIES[0])}

        <Form.Item
          name="static_collection_ids"
          label={t('user_catalog.publications.fields.collections')}
        >
          <Select
            {...collectionSelectProps}
            mode="multiple"
            showSearch
            allowClear
            placeholder={t(
              'user_catalog.publications.fields.collections_placeholder'
            )}
            filterOption={false}
          />
        </Form.Item>
        {excludedSelect(EXCLUSION_FAMILIES[1])}

        <Form.Item
          name="static_custom_tag_ids"
          label={t('user_catalog.publications.fields.tags')}
        >
          <Select
            {...tagSelectProps}
            mode="multiple"
            showSearch
            allowClear
            placeholder={t('user_catalog.publications.fields.tags_placeholder')}
            filterOption={false}
          />
        </Form.Item>
        {excludedSelect(EXCLUSION_FAMILIES[2])}

        {/* Search */}
        <Divider orientation="left">
          {t('user_catalog.common.tabs.search')}
        </Divider>

        <SearchPopularityField />
      </Form>
    </Edit>
  );
};
